/**
 * ericConversacionesService — fábrica de servicio para el API de conversaciones persistentes
 * de ERIC. Recibe un AjaxHandler ya suscripto (el del componente) para que el lifecycle
 * subscribe/unsubscribe sea responsabilidad del componente, no del servicio.
 *
 * Uso:
 *   var svc = crearEricConversacionesService(this.ajaxHandler);
 *   svc.listar(1, 20).then(function(res) { ... });
 *   svc.obtener(42).then(function(conv) { ... });
 *   svc.eliminar(42).then(function() { ... });
 *
 * docker exec -it vecfleet-web-workspace-1 bash -c "cd /var/www && CI=true npm test -- --testPathPattern='ericConversacionesService'"
 */

/**
 * Normaliza un turno del backend al shape usado por el panel de mensajes del componente.
 *
 * Cada turno persistido en `eric_mensajes` representa un par pregunta/respuesta + modo.
 * El modo se lee directamente del mensaje (`msgBackend.modo`): una misma conversación
 * puede mezclar turnos analyst y assistant.
 *
 * Backend → Frontend:
 *   { id, pregunta, respuesta, modo, payload_extra?, opinion?, created_at }
 *     →
 *   { id, modo, pregunta, respuesta, columnas, filas, totalFilas, advertencia,
 *     tipoVisualizacion, opinion, error, fechaEnvio }
 *
 * Si payload_extra es null o undefined, usa defaults vacíos.
 *
 * @param {object} msgBackend  - turno crudo del backend
 * @returns {object}
 */
function normalizarMensaje(msgBackend) {
  var extra = msgBackend.payload_extra || {};
  var respuestaTexto = msgBackend.respuesta != null ? msgBackend.respuesta : null;
  var modo = msgBackend.modo || 'analyst';
  // assistant y rechazado comparten shape de texto plano: no traen tabla ni SQL.
  var modoTextoPlano = (modo === 'assistant' || modo === 'rechazado');

  return {
    id: msgBackend.id != null ? msgBackend.id : null,
    modo: modo,
    pregunta: msgBackend.pregunta || '',
    respuesta: respuestaTexto,
    columnas: modoTextoPlano ? [] : (extra.columnas ? extra.columnas : []),
    filas: modoTextoPlano ? [] : (extra.filas ? extra.filas : []),
    totalFilas: modoTextoPlano ? 0 : (extra.total_filas ? extra.total_filas : 0),
    advertencia: modoTextoPlano ? null : (extra.advertencia ? extra.advertencia : null),
    tipoVisualizacion:
      modoTextoPlano ? null : (extra.tipo_visualizacion ? extra.tipo_visualizacion : null),
    opinion: msgBackend.opinion ? msgBackend.opinion : null,
    error: null,
    fechaEnvio: msgBackend.created_at || null,
  };
}

/**
 * Crea el servicio de conversaciones.
 *
 * @param {object} ajaxHandler - instancia de AjaxHandler ya suscripta en el componente
 * @returns {{ listar: Function, obtener: Function, eliminar: Function }}
 */
function crearEricConversacionesService(ajaxHandler) {
  /**
   * Lista paginada de conversaciones del usuario.
   *
   * @param {number} pagina     - página (default 1)
   * @param {number} porPagina  - items por página (default 20, max 100)
   * @returns {Promise<{ conversaciones: Array, paginacion: object }>}
   */
  function listar(pagina, porPagina) {
    var p = pagina || 1;
    var pp = porPagina || 20;
    return ajaxHandler
      .fetch('/eric/conversaciones?pagina=' + p + '&por_pagina=' + pp, {
        method: 'GET',
        silent: true,
      })
      .then(function (response) {
        if (!response || response.status !== 200) {
          return { conversaciones: [], paginacion: { pagina: 1, por_pagina: 20, total: 0 } };
        }
        return response.json().then(function (data) {
          var payload = data && data.data ? data.data : {};
          return {
            conversaciones: payload.conversaciones ? payload.conversaciones : [],
            paginacion: payload.paginacion
              ? payload.paginacion
              : { pagina: 1, por_pagina: 20, total: 0 },
          };
        });
      })
      .catch(function () {
        return { conversaciones: [], paginacion: { pagina: 1, por_pagina: 20, total: 0 } };
      });
  }

  /**
   * Obtiene el detalle de una conversación con sus mensajes normalizados.
   *
   * El modo se persiste por mensaje (cada turno trae el suyo); no hay un modo
   * único por conversación.
   *
   * @param {number} id
   * @returns {Promise<{ id, titulo, mensajes: Array }>}
   */
  function obtener(id) {
    return ajaxHandler
      .fetch('/eric/conversaciones/' + id, {
        method: 'GET',
        silent: true,
      })
      .then(function (response) {
        if (!response || response.status !== 200) {
          return Promise.reject(new Error('No se pudo cargar la conversación.'));
        }
        return response.json().then(function (data) {
          var conv = data && data.data ? data.data : {};
          var mensajesNormalizados = (conv.mensajes || []).map(function (msg) {
            return normalizarMensaje(msg);
          });
          return {
            id: conv.id,
            titulo: conv.titulo || '',
            mensajes: mensajesNormalizados,
          };
        });
      });
  }

  /**
   * Elimina (soft delete) una conversación.
   *
   * @param {number} id
   * @returns {Promise<void>}
   */
  function eliminar(id) {
    return ajaxHandler
      .fetch('/eric/conversaciones/' + id, {
        method: 'DELETE',
        silent: true,
      })
      .then(function (response) {
        if (!response || (response.status !== 200 && response.status !== 204)) {
          return Promise.reject(new Error('No se pudo eliminar la conversación.'));
        }
        return undefined;
      });
  }

  /**
   * Elimina (soft delete) todas las conversaciones del usuario.
   *
   * @returns {Promise<void>}
   */
  function eliminarTodas() {
    return ajaxHandler
      .fetch('/eric/conversaciones', {
        method: 'DELETE',
        silent: true,
      })
      .then(function (response) {
        if (!response || (response.status !== 200 && response.status !== 204)) {
          return Promise.reject(new Error('No se pudieron eliminar las conversaciones.'));
        }
        return undefined;
      });
  }

  return {
    listar: listar,
    obtener: obtener,
    eliminar: eliminar,
    eliminarTodas: eliminarTodas,
  };
}

export { normalizarMensaje };
export default crearEricConversacionesService;
